// @wc-ignore-file
import { type UseMutationResult, useQuery } from "@tanstack/react-query";
import {
  type LinearMappingsAnswer,
  type LinearSendInput,
  type LinearSendOutcome,
  fetchLinearMappings,
} from "./linear-context-api";

/** Every Linear query of a project (the integrations editor invalidates it on save). */
export const linearProjectKey = (projectId: string) => ["linear", projectId] as const;

/**
 * A send bound to one CR: the owner of the mutation (the CR section) decides
 * what it invalidates, so this feature never imports change-requests.
 */
export type LinearSendMutation = UseMutationResult<
  LinearSendOutcome,
  Error,
  Omit<LinearSendInput, "containerId" | "crId">
>;

/** CR-239: the project's enabled Linear mappings (what the send dialog may offer). */
const linearMappingsQuery = (projectId: string) => ({
  queryKey: [...linearProjectKey(projectId), "mappings"],
  queryFn: () => fetchLinearMappings(projectId),
  staleTime: 60 * 1000,
  retry: false,
});

export function useLinearMappings(projectId: string) {
  return useQuery({
    ...linearMappingsQuery(projectId),
    select: (answer: LinearMappingsAnswer) => answer.mappings,
  });
}

/**
 * CR-268: false once Linear is disconnected (the mappings and links stay). Same
 * request as useLinearMappings; undefined while pending or failed.
 */
export function useLinearConnected(projectId: string) {
  return useQuery({
    ...linearMappingsQuery(projectId),
    select: (answer: LinearMappingsAnswer) => answer.connected,
  });
}
